import {
  AlertLooks,
  ButtonLooks,
  FontIcons,
  StandardAlert,
  StandardButton,
  StandardTable,
  StandardTableColumn,
  StandardTableRow
} from '@brandfolder/react';
import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { MAX_PAGE_SIZE, useFetch } from '@api/ApiHelper';
import { AccessRequestsGetResponse, RequestableResourceGetResponse } from '@api/v4/users/access_requests';
import { Sizes, StandardCheckbox } from '@components/library/checkbox';
import { StandardDialog } from '@components/library/dialog';
import { DangerButton, TextButton } from '@components/library/button';
import { GrantAccessRequestForm } from './GrantAccessRequestForm';
import { ServerResourceType } from './resource-sidebar';

import './styles/access-requests.scss';

interface AccessRequestsProps {
  selectedResourceKey: string;
  selectedResourceType: ServerResourceType,
  setReloadPendingInvitations: SetStateDispatch<boolean>,
  setReloadPlanLimits: SetStateDispatch<boolean>,
  setReloadUsers: SetStateDispatch<boolean>
}

export const AccessRequests: FunctionComponent<AccessRequestsProps> = ({
  selectedResourceKey,
  selectedResourceType,
  setReloadPendingInvitations,
  setReloadPlanLimits,
  setReloadUsers
}) => {
  const [accessRequestsData, setAccessRequestData] = useState([]);
  const [accessRequestsDeleteKey, setAccessRequestsDeleteKey] = useState<string | undefined>();
  const [accessRequestPrompt, setAccessRequestPrompt] = useState('');
  const [deleteAccessRequestsOpen, setDeleteAccessRequestsOpen] = useState(false);
  const [grantedAccessRequest, setGrantedAccessRequest] = useState(false);
  const [isChecked, setIsChecked] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [showError, setShowError] = useState(false);

  const accessRequestsFetch = useFetch<AccessRequestsGetResponse>({
    url: `/api/v4/${selectedResourceType}s/${selectedResourceKey}/access_requests`,
    params: {
      per: MAX_PAGE_SIZE
    }
  });

  const accessRequestPromptFetch = useFetch<RequestableResourceGetResponse>({
    url: `/api/v4/${selectedResourceType}s/${selectedResourceKey}`,
    fields: 'access_request_prompt',
  });

  const accessRequestsDelete = useFetch({
    url: `/api/v4/access_requests/${accessRequestsDeleteKey}`,
    fetchOnMount: false,
    method: 'DELETE'
  });

  useEffect(() => {
    if (accessRequestsFetch.response) {
      setAccessRequestData(accessRequestsFetch.response?.data );
    }
  }, [accessRequestsFetch.response]);

  useEffect(() => {
    if (accessRequestPromptFetch.response) {
      setAccessRequestPrompt(accessRequestPromptFetch.response?.data.attributes.access_request_prompt);
    }
  }, [accessRequestPromptFetch.response]);

  useEffect(() => {
    if (accessRequestsDeleteKey) {
      setDeleteAccessRequestsOpen(true);
    }
  }, [accessRequestsDeleteKey]);

  useEffect(() => {
    if (accessRequestsDelete.response) {
      setAccessRequestsDeleteKey(undefined);
      setDeleteAccessRequestsOpen(!deleteAccessRequestsOpen);
      accessRequestsFetch.fetch();
    }
  }, [accessRequestsDelete.response]);

  const handleSelect = (event): void => {
    const { id, checked } = event.target;
    if (checked) {
      setIsChecked([...isChecked, id]);
    } else {
      setIsChecked(isChecked.filter((item) => item !== id));
    }
  };

  const selectAllChecked = isChecked.length > 0 && isChecked.length === accessRequestsData.length;

  const handleSelectAll = (): void => {
    if (selectAllChecked) {
      setIsChecked([]);
    } else {
      setIsChecked(accessRequestsData.map((request) => request.id));
    }
  };

  const selectedEmails = accessRequestsData.filter((request)=> isChecked.includes(request.id)).map((request) => request.attributes.email);

  const accessRequestsDeleteConfirmationDialog = (
    <StandardDialog
      dialogHeaderClassName="access-requests-delete-confirmation-header"
      footer={
        <DangerButton
          isLoading={accessRequestsDelete.loading}
          onClick={(): void => {
            accessRequestsDelete.fetch();
          }}
        >
          {bfTranslate('Delete access request')}
        </DangerButton>
      }
      id="access-requests-delete-confirmation-dialog"
      open={deleteAccessRequestsOpen}
      setOpen={setDeleteAccessRequestsOpen}
      showFooter
      title={bfTranslate('Delete access request')}
      titleIcon={`bff-${FontIcons.Trash}`}
    >
      <p>{bfTranslate('Are you sure you want to delete this access request?')}</p>
    </StandardDialog>
  );

  const columns: StandardTableColumn[] = [{
    children: (
      <StandardCheckbox
        checked={selectAllChecked}
        id="select-all"
        name={bfTranslate('Select all')}
        onChange={(): void => handleSelectAll()}
        partiallyChecked={isChecked.length > 0 && isChecked.length !== accessRequestsData.length}
        size={Sizes.Medium}
      />
    ),
    heading: bfTranslate('Select all'),
    rowKey: 'checkbox'
  }, {
    children: bfTranslate('Email'),
    heading: bfTranslate('Email'),
    rowKey: 'email',
    width: '50%'
  }, !!accessRequestPrompt && {
    children: bfTranslate('Description'),
    heading:  bfTranslate('Description'),
    maxWidth: '425px',
    rowKey: 'promptRequest',
    width: '50%'
  }, {
    rowKey: 'removeAccessRequestIcon'
  }];

  const rows: StandardTableRow[] = accessRequestsFetch.response ? accessRequestsFetch.response.data.map((accessRequest) => ({
    checkbox: (
      <StandardCheckbox
        checked={isChecked.includes(accessRequest.id)}
        className='access-request-checkbox'
        id={accessRequest.id}
        name={bfTranslate('Select individual checkbox')}
        onChange={(): void => handleSelect(event)}
        size={Sizes.Medium}
      />
    ),
    email: accessRequest.attributes.email,
    promptRequest: accessRequest.attributes.prompt_response,
    removeAccessRequestIcon: (
      <StandardButton
        aria-label={bfTranslate('Remove access request')}
        className="access-request-remove-icon"
        look={ButtonLooks.Warning}
        onClick={(): void => {
          setDeleteAccessRequestsOpen(true);
          setAccessRequestsDeleteKey(accessRequest.id);
        }}
        startIcon={FontIcons.Remove}
      >
      </StandardButton>
    )
  })) : [];

  return (rows.length > 0 || grantedAccessRequest) && (
    <div
      className={classnames(
        'access-requests-container',
        'user-settings__segment',
        { 'no-access-request-prompt': !accessRequestPrompt }
      )}
    >
      { accessRequestsFetch.response && accessRequestsDeleteConfirmationDialog }
      <h3>{bfTranslate('Access Requests')}</h3>
      <>
      {showAlert && (
        <StandardAlert
          className="access-requests-container__submission-alert"
          look={AlertLooks.Success}
        >
          {bfTranslate("We're working on creating invitations. You'll receive a notification when the invitations have been sent.")}
        </StandardAlert>
      )}
      {showError && (
        <StandardAlert
          className="access-requests-container__submission-alert"
          look={AlertLooks.Error}
        >
          {bfTranslate('Something went wrong while creating invitations. Try again or contact customer support.')}
        </StandardAlert>
      )}
    </>
      <StandardTable
        caption={bfTranslate('Access requests Table')}
        columns={columns}
        empty={rows.length === 0}
        emptyContent={bfTranslate('There are no access requests')}
        error={!!accessRequestsFetch.error}
        errorContent={(
          <div>
            <p>{bfTranslate('Oops! Something went wrong.')}</p>
            <TextButton onClick={(): Promise<void> => accessRequestsFetch.fetch()}>
              {bfTranslate('Try again')}
            </TextButton>
          </div>
        )}
        id="access-requests-table"
        loading={accessRequestsFetch.loading}
        rows={rows}
        scrollY
      />
      <GrantAccessRequestForm
        accessRequestFetch={accessRequestsFetch.fetch}
        selectedEmails={selectedEmails}
        selectedResourceKey={selectedResourceKey}
        selectedResourceType={selectedResourceType}
        setGrantedAccessRequest={setGrantedAccessRequest}
        setReloadPendingInvitations={setReloadPendingInvitations}
        setReloadPlanLimits={setReloadPlanLimits}
        setReloadUsers={setReloadUsers}
        setShowAlert={setShowAlert}
        setShowError={setShowError}
      />
    </div>
  );
};